import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { Link } from "wouter";
import { ArrowLeft, Award, CheckCircle, ExternalLink } from "lucide-react";

const TIERS = [
  {
    title: "MRPharmS — Member of the Royal Pharmaceutical Society",
    badge: "Membership",
    badgeColor: "bg-violet-100 text-violet-700",
    cost: "~£260/year (employed pharmacist, 2025)",
    desc: "Full membership of the RPS. Open to all GPhC-registered pharmacists and pharmacy technicians in Great Britain. The professional home of the pharmacy profession.",
    benefits: [
      "Access to the RPS library, Pharmaceutical Journal, and clinical resources",
      "Eligibility for RPS credentialing (Advanced, Consultant)",
      "Support for RPS Faculty portfolio (APPF tracker)",
      "Professional indemnity support and career guidance",
      "RPS networks — including rheumatology and specialist practice",
      "RPS governance — voting rights, committee involvement",
    ],
    howTo: [
      "Apply via rpharms.com/membership",
      "Select 'Pharmacist' membership category",
      "Provide GPhC registration number",
      "Payment by direct debit; subscription renews annually",
    ],
  },
  {
    title: "FRPharmS — Fellow of the Royal Pharmaceutical Society",
    badge: "Fellowship",
    badgeColor: "bg-blue-100 text-blue-700",
    cost: "No additional fee beyond MRPharmS",
    desc: "Fellowship recognises sustained excellence and contribution to the pharmacy profession. Awarded by the RPS in recognition of significant professional achievement.",
    benefits: [
      "Recognition of career-long contribution to pharmacy",
      "Use of post-nominal 'FRPharmS'",
      "Enhanced professional standing in academic, regulatory, and senior NHS settings",
      "Fellowship awarded by the RPS Council",
    ],
    howTo: [
      "Must be a full RPS Member (MRPharmS)",
      "Submit a detailed application demonstrating sustained contribution to pharmacy (clinical, education, research, or policy)",
      "Two senior referees (at least one FRPharmS or equivalent)",
      "Applications reviewed by the Fellowship panel; decisions communicated within 3 months",
      "Applications open on a rolling basis via rpharms.com/fellowship",
    ],
    eligibility: "Minimum 10 years post-qualification experience; sustained and demonstrable contribution to pharmacy at national or international level",
  },
  {
    title: "RPS Faculty — Advanced or Consultant Credentialing",
    badge: "Credentialing",
    badgeColor: "bg-emerald-100 text-emerald-700",
    cost: "Assessment fee: ~£850–£1,100 (2025); employer-funded in many trusts",
    desc: "The RPS Faculty credentialing programme validates advanced and consultant-level pharmacy practice using the Advanced Pharmacy Practice Framework (APPF). Not to be confused with academic faculty — this is a professional credentialing system.",
    benefits: [
      "Nationally recognised credential: 'RPS Advanced Pharmacist' or 'Consultant Pharmacist'",
      "Validates your APPF portfolio against national standards",
      "Increasingly required or preferred for Band 8a+ roles",
      "Access to RPS Faculty mentor matching and development support",
      "Demonstrates commitment to advancing pharmacy practice",
    ],
    howTo: [
      "Must be RPS Member (MRPharmS)",
      "Build your APPF portfolio using the RPS Faculty portfolio system (or the APPF tracker in RheumHub)",
      "Register for credentialing via the RPS Faculty portal",
      "Submit your portfolio and attend an assessor interview",
      "Receive written feedback and credentialing decision",
      "Recredentialing required every 5 years",
    ],
    eligibility: "Typically Band 7–8a for Advanced; Band 8b+ for Consultant credentialing",
  },
];

export default function RpsMembership() {
  return (
    <PharmacistLayout title="RPS Membership & Fellowship">
      <div className="space-y-6 max-w-4xl">
        <Link href="/pharmacist/career">
          <button className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Career Pathways
          </button>
        </Link>

        <div className="bg-gradient-to-br from-violet-900 to-violet-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Award className="h-7 w-7 text-violet-300" />
            <div>
              <h2 className="text-xl font-bold">RPS Membership & Fellowship</h2>
              <p className="text-violet-300 text-sm">Royal Pharmaceutical Society · MRPharmS, FRPharmS & RPS Faculty</p>
            </div>
          </div>
          <p className="text-violet-200 text-sm leading-relaxed">
            The Royal Pharmaceutical Society (RPS) is the professional body for pharmacists and pharmacy in Great Britain. Membership, Fellowship, and Faculty credentialing are the three main ways pharmacists formally affiliate with and are recognised by the RPS.
          </p>
        </div>

        {TIERS.map((tier) => (
          <div key={tier.title} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100">
              <div className="flex items-start gap-3">
                <span className={`shrink-0 text-xs font-bold px-2.5 py-1 rounded-full ${tier.badgeColor} mt-0.5`}>
                  {tier.badge}
                </span>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-900 leading-snug">{tier.title}</h3>
                  <p className="text-sm text-slate-500 mt-0.5">Annual cost: {tier.cost}</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mt-3">{tier.desc}</p>
              {tier.eligibility && (
                <p className="text-xs text-violet-700 bg-violet-50 rounded-lg px-3 py-2 border border-violet-200 mt-3">
                  <strong>Eligibility:</strong> {tier.eligibility}
                </p>
              )}
            </div>
            <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Benefits</h4>
                <ul className="space-y-1.5">
                  {tier.benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                      <CheckCircle className="h-3.5 w-3.5 text-violet-500 shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">How to Apply</h4>
                <ol className="space-y-1.5">
                  {tier.howTo.map((step, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                      <span className="shrink-0 h-4 w-4 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        ))}

        {/* Cost comparison */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h4 className="font-bold text-slate-900 mb-3">Employer Funding</h4>
          <p className="text-sm text-slate-600 leading-relaxed">
            Many NHS trusts will fund RPS membership and RPS Faculty credentialing as part of the pharmacist's study leave and CPD allowance. For Band 8a+ posts, RPS membership is often an expectation or a requirement listed in the job description. Discuss with your manager or educational supervisor how to access trust funding for:
          </p>
          <ul className="mt-3 space-y-1.5">
            {[
              "Annual RPS membership subscription (MRPharmS)",
              "RPS Faculty credentialing assessment fees",
              "RPS conference attendance and training events",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                <CheckCircle className="h-4 w-4 text-violet-500 shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h4 className="font-bold text-slate-900 mb-3">Useful Links</h4>
          <div className="space-y-2">
            {[
              { label: "RPS Membership — rpharms.com/membership", url: "https://www.rpharms.com/membership" },
              { label: "RPS Fellowship — rpharms.com/fellowship", url: "https://www.rpharms.com/fellowship" },
              { label: "RPS Faculty & Credentialing — rpharms.com/development/rps-faculty", url: "https://www.rpharms.com/development/rps-faculty" },
              { label: "Advanced Pharmacy Practice Framework (APPF)", url: "https://www.rpharms.com/resources/frameworks/advanced-pharmacy-practice-framework" },
            ].map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-800 transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </PharmacistLayout>
  );
}
